import { BLUE_100, BLUE_300, GRAY_400, GRAY_900 } from 'constants/styling/theme'
import React, { useCallback, useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { Box } from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { MUIDivider } from 'components/common/MUIDivider'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import styles from './ParcelViewerMap.module.sass'

export enum ParcelChangeType {
  SELECTED = 'SELECTED',
  UNSELECTED = 'UNSELECTED',
}

/** Represents a hierarchical structure of parcels grouped by district and section. */
export type GroupedParcels = Record<
  string, // District
  Record<
    string, // Section, also know as "hallway"
    string[] // Array of parcel numbers
  >
>

/** @interface Props for the ParcelViewerMap component. */
interface Props {
  /** The latitude and longitude of the selected property location. */
  selectedPropertyLocation?: { lat: number, lng: number }
  /** Callback function triggered when the parcel changes. */
  onParcelChange?: (groupedParcels: GroupedParcels) => void
  /** Callback function triggered when the parcel viewer popup is opened. */
  onOpenParcelViewerPopup?: () => void
}

/** @interface Parcel */
export interface Parcel {
  /** A unique ID for the parcel entity. */
  id: string
  /** The cadastral district where the parcel is located. */
  cadastralDistrict: string
  /** The parcel number or identifier within the district. */
  parcel: string
  /** The section of the cadastral district where the parcel is situated. */
  section: string
  /** A unique identifier for the parcel. */
  parcelId: string
  /** The state or region where the parcel is located. */
  state: string
  /** The geometric representation of the parcel, typically in a spatial format. */
  geometry: string
}

/**
 * @component
 * ParcelViewerMap integrates an iframe-based parcel map viewer into the application.
 * Visible in the Property Information step of the Purchase Flow.
 *
 * @example
 * ```tsx
 * <ParcelViewerMap
 *   selectedPropertyLocation={{ lat: 40.7128, lng: -74.0060 }}
 *   onParcelChange={(groupedData) => console.log(groupedData)}
 * />
 * ```
 */
export const ParcelViewerMap: React.FC<Props> = ({
  selectedPropertyLocation,
  onParcelChange,
  onOpenParcelViewerPopup
}) => {

  const { t } = useTranslation(['purchase_flow'])

  const parcelViewerUrl = useMemo(() => `${process.env.REACT_APP_DOCESTATE_MAP_URL}?lat=${selectedPropertyLocation?.lat ?? 0}&lng=${selectedPropertyLocation?.lng ?? 0}`, [selectedPropertyLocation])

  // Request the selected parcels from the map viewer - not used in the current implementation
  const requestParcelSelection = useCallback((frame: HTMLIFrameElement | null) => {
    if (frame instanceof HTMLIFrameElement && frame.contentWindow) {
      frame.contentWindow.postMessage({ type: 'getSelectedParcels' }, '*')
    }
  }, [])

  const handleSelectedParcels = (payload: string[]) => {

    const selectedParcels = payload.map((data) => data.split('_')) // The format received is "district_section_parcelNumber"

    const groupedData = selectedParcels.reduce((acc, [district, section, parcelNumber]) => {
      // Ensure the district exists
      if (!acc[district]) {
        acc[district] = {}
      }

      // Ensure the section exists
      if (!acc[district][section]) {
        acc[district][section] = []
      }

      // Add the parcel number to the section
      acc[district][section].push(parcelNumber)

      return acc
    }, {} as GroupedParcels)

    onParcelChange?.(groupedData)
  }

  useEffect(() => {
    const frame = document.getElementById('parcel-viewer') as HTMLIFrameElement | null

    window.addEventListener('message', (e) => {
      if (e.origin !== process.env.REACT_APP_DOCESTATE_MAP_URL) return

      const { type, payload } = e.data

      switch (type) {
        case 'selectedParcels':
          if (Array.isArray(payload)) { // string[], else Property
            handleSelectedParcels(payload)
          }
          break
        case 'parcelChange': // single parcel toggled
          requestParcelSelection(frame) // Triggers a request to get the selected parcels
          break
        case 'error':
          console.error('Viewer error:', payload)
          break
      }
    })

    // Cleanup listener to prevent memory leaks
    return () => {
      window.removeEventListener('message', () => { })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <SectionedBorderBox
      marginBottom={2}
      title={
        <Stack direction="row" justifyContent="flex-start" alignItems="center" gap="1rem">

          <Typography color={GRAY_900} variant="text-lg" fontWeight={600}>
            {t('property_information_step.parcel_map_viewer')}
          </Typography>

          <Box onClick={onOpenParcelViewerPopup} sx={{ cursor: 'pointer' }}>
            <HelpOutlineIcon fontSize="large" sx={{ color: GRAY_900 }} />
          </Box>

        </Stack>
      }
    >
      {/** DOCESTATE PARCEL VIEWER */}
      <iframe
        id="parcel-viewer"
        src={parcelViewerUrl}
        className={styles.parcelViewer}
        title="Parcel Map Viewer">
      </iframe>

      <BorderBoxWrapper backgroundColor={BLUE_100} borderColor={BLUE_300} padding={1.5} marginTop="2rem">

        <Stack gap=".6rem">

          <Typography variant="text-sm" color={GRAY_900}>
            <Trans t={t} i18nKey="property_information_step.parcel_map_instruction.content" />
          </Typography>

          <MUIDivider margin={8} sx={{ borderColor: `${GRAY_400} !important` }} />

          <Typography variant="text-sm" color={GRAY_900}>
            <Trans t={t} i18nKey="property_information_step.parcel_map_instruction.important_note" />
          </Typography>

        </Stack>

      </BorderBoxWrapper>

    </SectionedBorderBox>
  )
}
